import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link, useOutletContext } from 'react-router-dom';
import { api } from '../../services/api';
import type { Post, Media } from '../../types/api';
import { convertToWebP, formatBytes } from '../../utils/imageOptimizer';

export const AdminPostEditPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isNew = !id || id === 'new';
    const navigate = useNavigate();
    const { setFlashMessage } = useOutletContext<{ setFlashMessage: (msg: any) => void }>();

    const [form, setForm] = useState<Partial<Post>>({
        title: '',
        slug: '',
        excerpt: '',
        body: '',
        category_id: null,
        status: 'draft',
        featured: false,
        reading_time_minutes: 3,
        meta_title: '',
        meta_description: '',
        cover_image: '',
    });
    const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
    const [loading, setLoading] = useState<boolean>(!isNew);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Image Upload & WebP Optimization State
    const [uploadingCover, setUploadingCover] = useState<boolean>(false);
    const [coverStats, setCoverStats] = useState<{ original: number; converted: number; savings: number } | null>(null);
    const [uploadingInline, setUploadingInline] = useState<boolean>(false);
    const [inlineStats, setInlineStats] = useState<{ original: number; converted: number; savings: number } | null>(null);

    // Media Library Modal for Post
    const [showMediaModal, setShowMediaModal] = useState<boolean>(false);
    const [mediaItems, setMediaItems] = useState<Media[]>([]);
    const [mediaSearch, setMediaSearch] = useState<string>('');
    const [loadingMedia, setLoadingMedia] = useState<boolean>(false);

    const coverInputRef = useRef<HTMLInputElement>(null);
    const inlineInputRef = useRef<HTMLInputElement>(null);
    const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (!isNew && id) {
            api.adminGetPost(parseInt(id, 10)).then(res => {
                if (res.data?.post) {
                    setForm(res.data.post);
                    if (res.data.categories) setCategories(res.data.categories);
                }
            }).catch(err => {
                setError(err.message || 'Failed to load post.');
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [id, isNew]);

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const title = e.target.value;
        if (isNew) {
            const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            setForm(prev => ({ ...prev, title, slug }));
        } else {
            setForm(prev => ({ ...prev, title }));
        }
    };

    /**
     * Automatic WebP Conversion and Upload for Article Cover Image
     */
    const handleCoverImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingCover(true);
        setError(null);

        try {
            // 1. Client-side instant WebP conversion (lossless or 85% high quality clamped to 1920px)
            const webp = await convertToWebP(file, { maxWidth: 1920, quality: 0.85 });

            // 2. Upload WebP payload
            const res = await api.adminUploadMedia({
                filename: webp.filename,
                original_name: webp.originalName,
                data: webp.dataUrl,
                width: webp.width,
                height: webp.height,
                size_bytes: webp.sizeBytes,
                alt_text: form.title || webp.originalName.replace(/\.[^/.]+$/, ''),
            });

            const uploadedUrl = res.data?.media?.url || `/storage/${res.data?.media?.path}`;
            setForm(prev => ({ ...prev, cover_image: uploadedUrl }));
            setCoverStats({
                original: webp.originalSizeBytes,
                converted: webp.sizeBytes,
                savings: webp.savingsPercent,
            });

            setFlashMessage({
                type: 'success',
                text: `Cover image converted to WebP successfully! (${formatBytes(webp.originalSizeBytes)} → ${formatBytes(webp.sizeBytes)}, saved ${webp.savingsPercent}%)`
            });
        } catch (err: any) {
            setError(err.message || 'Failed to convert and upload cover image.');
        } finally {
            setUploadingCover(false);
            if (coverInputRef.current) coverInputRef.current.value = '';
        }
    };

    /**
     * Helper to insert text/HTML into Article Body at cursor position
     */
    const insertIntoBodyAtCursor = (snippet: string) => {
        const textarea = bodyTextareaRef.current;
        if (!textarea) {
            setForm(prev => ({ ...prev, body: (prev.body || '') + snippet }));
            return;
        }

        const start = textarea.selectionStart ?? (form.body || '').length;
        const end = textarea.selectionEnd ?? start;
        const current = form.body || '';
        const updated = current.substring(0, start) + snippet + current.substring(end);

        setForm(prev => ({ ...prev, body: updated }));

        setTimeout(() => {
            textarea.focus();
            const newCursor = start + snippet.length;
            textarea.setSelectionRange(newCursor, newCursor);
        }, 50);
    };

    /**
     * Automatic WebP Conversion and Direct Insertion into Article Body
     */
    const handleInlineImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingInline(true);
        setError(null);

        try {
            // 1. Client-side automatic WebP conversion
            const webp = await convertToWebP(file, { maxWidth: 1600, quality: 0.85 });

            // 2. Upload WebP payload
            const res = await api.adminUploadMedia({
                filename: webp.filename,
                original_name: webp.originalName,
                data: webp.dataUrl,
                width: webp.width,
                height: webp.height,
                size_bytes: webp.sizeBytes,
                alt_text: webp.originalName.replace(/\.[^/.]+$/, ''),
            });

            const uploadedUrl = res.data?.media?.url || `/storage/${res.data?.media?.path}`;
            const caption = window.prompt('Enter image caption or description (optional):', '') || '';

            // 3. Build responsive figure HTML snippet
            const snippet = `\n<figure class="my-8">\n    <img src="${uploadedUrl}" alt="${caption || 'Article image'}" class="rounded-2xl w-full max-h-[500px] object-cover shadow-xl border border-slate-800" loading="lazy" />\n${caption ? `    <figcaption class="text-xs text-center text-slate-400 mt-2.5 font-medium">${caption}</figcaption>\n` : ''}</figure>\n`;

            insertIntoBodyAtCursor(snippet);

            setInlineStats({
                original: webp.originalSizeBytes,
                converted: webp.sizeBytes,
                savings: webp.savingsPercent,
            });

            setFlashMessage({
                type: 'success',
                text: `Image converted to WebP (${formatBytes(webp.originalSizeBytes)} → ${formatBytes(webp.sizeBytes)}, saved ${webp.savingsPercent}%) and inserted into article!`
            });
        } catch (err: any) {
            setError(err.message || 'Failed to convert and upload inline image.');
        } finally {
            setUploadingInline(false);
            if (inlineInputRef.current) inlineInputRef.current.value = '';
        }
    };

    /**
     * Open Media Library modal to pick previously uploaded images
     */
    const openMediaLibraryModal = async () => {
        setShowMediaModal(true);
        setLoadingMedia(true);
        try {
            const res = await api.adminGetMedia(1);
            setMediaItems(res.data || []);
        } catch {
            // error handled
        } finally {
            setLoadingMedia(false);
        }
    };

    const handleSelectMediaItem = (item: Media) => {
        const imgUrl = item.url || `/storage/${item.path}`;
        const snippet = `\n<figure class="my-8">\n    <img src="${imgUrl}" alt="${item.alt_text || 'Article image'}" class="rounded-2xl w-full max-h-[500px] object-cover shadow-xl border border-slate-800" loading="lazy" />\n${item.alt_text ? `    <figcaption class="text-xs text-center text-slate-400 mt-2.5 font-medium">${item.alt_text}</figcaption>\n` : ''}</figure>\n`;
        insertIntoBodyAtCursor(snippet);
        setShowMediaModal(false);
        setFlashMessage({ type: 'success', text: 'Media image inserted into article body!' });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            await api.adminSavePost(isNew ? null : parseInt(id, 10), form);
            setFlashMessage({
                type: 'success',
                text: `Article successfully ${isNew ? 'created' : 'updated'}.`
            });
            navigate('/admin/posts');
        } catch (err: any) {
            setError(err.message || 'Failed to save post.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="py-20 text-center text-slate-400 flex items-center justify-center gap-3">
                <span className="w-5 h-5 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin"></span>
                <span>Loading article details...</span>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-extrabold text-white tracking-tight">
                        {isNew ? 'Write New Article' : 'Edit Article'}
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Compose rich editorial content with automatic WebP image compression.
                    </p>
                </div>
                <Link to="/admin/posts" className="text-xs text-slate-400 hover:text-white transition">
                    &larr; Back to Articles
                </Link>
            </div>

            {error && (
                <div className="p-4 rounded-xl bg-rose-900/80 border border-rose-500/40 text-rose-200 text-xs font-semibold">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="p-8 rounded-3xl glass-card space-y-6">
                
                {/* 1. Article Title */}
                <div>
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Title</label>
                    <input 
                        type="text" 
                        required 
                        value={form.title || ''}
                        onChange={handleTitleChange}
                        className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm font-semibold"
                        placeholder="Article Title..." 
                    />
                </div>

                {/* 2. Cover / Featured Image Upload with Auto-WebP */}
                <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800/80 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div>
                            <span className="text-xs font-bold text-white uppercase tracking-wider block">Featured Cover Image</span>
                            <span className="text-[11px] text-slate-400 block mt-0.5">
                                Any image format (PNG, JPG, BMP) will automatically be converted to high-performance WebP.
                            </span>
                        </div>
                        {coverStats && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-950/80 border border-emerald-500/40 text-emerald-300 text-[11px] font-semibold">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                Converted to WebP • Saved {coverStats.savings}% ({formatBytes(coverStats.original)} → {formatBytes(coverStats.converted)})
                            </span>
                        )}
                    </div>

                    <input 
                        type="file" 
                        ref={coverInputRef} 
                        onChange={handleCoverImageUpload} 
                        accept="image/*" 
                        className="hidden" 
                        id="cover-upload-input" 
                    />

                    {form.cover_image ? (
                        <div className="relative group rounded-2xl overflow-hidden border border-slate-800 bg-slate-900">
                            <img 
                                src={form.cover_image.startsWith('/') ? form.cover_image : `/storage/${form.cover_image}`} 
                                alt="Cover preview" 
                                className="w-full h-56 object-cover" 
                            />
                            <div className="absolute inset-0 bg-slate-950/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-3 backdrop-blur-xs">
                                <button
                                    type="button"
                                    onClick={() => coverInputRef.current?.click()}
                                    disabled={uploadingCover}
                                    className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider transition cursor-pointer"
                                >
                                    {uploadingCover ? 'Converting to WebP...' : 'Replace Image'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setForm(prev => ({ ...prev, cover_image: '' }));
                                        setCoverStats(null);
                                    }}
                                    className="px-4 py-2 rounded-xl bg-rose-600/80 hover:bg-rose-500 text-white text-xs font-bold uppercase tracking-wider transition cursor-pointer"
                                >
                                    Remove
                                </button>
                            </div>
                            <div className="absolute bottom-3 left-3 bg-slate-950/80 px-2.5 py-1 rounded-md text-[10px] font-mono text-emerald-400 border border-emerald-500/30">
                                {form.cover_image}
                            </div>
                        </div>
                    ) : (
                        <div 
                            onClick={() => !uploadingCover && coverInputRef.current?.click()}
                            className={`border-2 border-dashed border-slate-800 hover:border-emerald-500/60 rounded-2xl p-6 text-center cursor-pointer transition bg-slate-900/30 hover:bg-slate-900/60 group ${uploadingCover ? 'opacity-60 pointer-events-none' : ''}`}
                        >
                            <div className="w-12 h-12 rounded-full bg-slate-900 group-hover:bg-emerald-950/60 border border-slate-800 group-hover:border-emerald-500/40 flex items-center justify-center mx-auto mb-3 transition">
                                {uploadingCover ? (
                                    <span className="w-5 h-5 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin"></span>
                                ) : (
                                    <svg className="w-6 h-6 text-slate-400 group-hover:text-emerald-400 transition" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                )}
                            </div>
                            <span className="text-xs font-bold text-white group-hover:text-emerald-300 block transition">
                                {uploadingCover ? 'Optimizing & Converting to WebP...' : 'Click to Upload Cover Image'}
                            </span>
                            <span className="text-[11px] text-slate-500 block mt-1">
                                Automatic WebP conversion with 85% compression quality & responsive clamping
                            </span>
                        </div>
                    )}
                </div>

                {/* 3. URL Slug & Status */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">URL Slug</label>
                        <input 
                            type="text" 
                            required 
                            value={form.slug || ''}
                            onChange={e => setForm({ ...form, slug: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-slate-300 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Publication Status</label>
                        <select 
                            value={form.status || 'draft'}
                            onChange={e => setForm({ ...form, status: e.target.value as any })}
                            className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                            <option value="archived">Archived</option>
                        </select>
                    </div>
                </div>

                {/* 4. Excerpt */}
                <div>
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Excerpt (Summary)</label>
                    <textarea 
                        rows={2}
                        value={form.excerpt || ''}
                        onChange={e => setForm({ ...form, excerpt: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="Brief summary for listings and search preview..."
                    />
                </div>

                {/* 5. Article Body with Dedicated Image Upload & Formatting Toolbar */}
                <div className="space-y-2">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                            Article Body (HTML/Markdown supported)
                        </label>
                        {inlineStats && (
                            <span className="text-[11px] text-emerald-400 font-medium">
                                ⚡ WebP image inserted (saved {inlineStats.savings}%)
                            </span>
                        )}
                    </div>

                    {/* Toolbar */}
                    <div className="p-2.5 rounded-t-2xl bg-slate-900 border border-slate-800 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                            {/* Hidden Inline Image Upload Input */}
                            <input 
                                type="file" 
                                ref={inlineInputRef} 
                                onChange={handleInlineImageUpload} 
                                accept="image/*" 
                                className="hidden" 
                                id="inline-image-upload" 
                            />

                            {/* Upload & Insert Image Button */}
                            <button
                                type="button"
                                onClick={() => inlineInputRef.current?.click()}
                                disabled={uploadingInline}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white transition shadow cursor-pointer ${uploadingInline ? 'opacity-50 pointer-events-none' : ''}`}
                                title="Upload any image and automatically convert to WebP before inserting"
                            >
                                {uploadingInline ? (
                                    <>
                                        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        <span>Converting WebP...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span>+ Upload Image (Auto-WebP)</span>
                                    </>
                                )}
                            </button>

                            {/* Media Library Button */}
                            <button
                                type="button"
                                onClick={openMediaLibraryModal}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 transition cursor-pointer"
                                title="Choose from existing media assets"
                            >
                                <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                                <span>Media Library</span>
                            </button>
                        </div>

                        {/* Formatting Snippet Shortcuts */}
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => insertIntoBodyAtCursor('\n<h2>Subheading Title</h2>\n')}
                                className="px-2 py-1 rounded text-xs font-mono font-bold bg-slate-950 text-slate-300 hover:text-white border border-slate-800 transition"
                                title="Insert Heading 2"
                            >
                                H2
                            </button>
                            <button
                                type="button"
                                onClick={() => insertIntoBodyAtCursor('\n<h3>Section Subheading</h3>\n')}
                                className="px-2 py-1 rounded text-xs font-mono font-bold bg-slate-950 text-slate-300 hover:text-white border border-slate-800 transition"
                                title="Insert Heading 3"
                            >
                                H3
                            </button>
                            <button
                                type="button"
                                onClick={() => insertIntoBodyAtCursor('\n<blockquote class="pl-4 border-l-2 border-emerald-500 text-slate-300 italic my-4">\n    Quote text goes here...\n</blockquote>\n')}
                                className="px-2 py-1 rounded text-xs font-mono bg-slate-950 text-slate-300 hover:text-white border border-slate-800 transition"
                                title="Insert Quote"
                            >
                                &ldquo;Quote&rdquo;
                            </button>
                            <button
                                type="button"
                                onClick={() => insertIntoBodyAtCursor('\n<div class="p-4 my-6 rounded-2xl bg-emerald-950/40 border border-emerald-500/30 text-emerald-200 text-sm">\n    <strong>Important Note:</strong> Callout content here.\n</div>\n')}
                                className="px-2 py-1 rounded text-xs font-mono bg-slate-950 text-slate-300 hover:text-white border border-slate-800 transition"
                                title="Insert Highlight Box"
                            >
                                Callout
                            </button>
                        </div>
                    </div>

                    {/* Textarea */}
                    <textarea 
                        ref={bodyTextareaRef}
                        rows={16}
                        required
                        value={form.body || ''}
                        onChange={e => setForm({ ...form, body: e.target.value })}
                        className="w-full px-4 py-3 rounded-b-2xl bg-slate-950 border border-slate-800 text-white font-mono text-xs placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="<p>Write your article content here...</p>"
                    />
                </div>

                {/* 6. Read Time & Featured Checkbox */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Estimated Read Time (Minutes)</label>
                        <input 
                            type="number" 
                            min={1}
                            value={form.reading_time_minutes || 3}
                            onChange={e => setForm({ ...form, reading_time_minutes: parseInt(e.target.value, 10) || 1 })}
                            className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                        />
                    </div>

                    <div className="flex items-center pt-6">
                        <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-300">
                            <input 
                                type="checkbox" 
                                checked={!!form.featured}
                                onChange={e => setForm({ ...form, featured: e.target.checked })}
                                className="rounded border-slate-700 bg-slate-900 text-emerald-500 focus:ring-emerald-500" 
                            />
                            <span>Feature this article on homepage</span>
                        </label>
                    </div>
                </div>

                {/* 7. SEO Meta Box */}
                <div className="pt-6 border-t border-slate-800 space-y-4">
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider">SEO & OpenGraph Metadata</h3>
                    
                    <div>
                        <label className="block text-xs font-semibold text-slate-400 mb-1">Meta Title</label>
                        <input 
                            type="text"
                            value={form.meta_title || ''}
                            onChange={e => setForm({ ...form, meta_title: e.target.value })}
                            className="w-full px-4 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="Custom title tag (defaults to Article Title)"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-400 mb-1">Meta Description</label>
                        <textarea 
                            rows={2}
                            value={form.meta_description || ''}
                            onChange={e => setForm({ ...form, meta_description: e.target.value })}
                            className="w-full px-4 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="Custom meta description for search results"
                        />
                    </div>
                </div>

                {/* Submit Controls */}
                <div className="pt-6 border-t border-slate-800 flex items-center justify-end gap-4">
                    <Link to="/admin/posts" className="px-5 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white transition">
                        Cancel
                    </Link>
                    <button 
                        type="submit" 
                        disabled={saving}
                        className="px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg transition cursor-pointer disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : (isNew ? 'Publish Article' : 'Save Changes')}
                    </button>
                </div>
            </form>

            {/* Media Library Selection Modal */}
            {showMediaModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
                        <div className="p-6 border-b border-slate-800 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-white">Media Library Assets</h3>
                                <p className="text-xs text-slate-400 mt-0.5">Click any WebP image to insert it directly into the article body.</p>
                            </div>
                            <button 
                                type="button" 
                                onClick={() => setShowMediaModal(false)}
                                className="text-slate-400 hover:text-white text-sm font-bold p-1"
                            >
                                &times;
                            </button>
                        </div>

                        <div className="p-4 border-b border-slate-800 bg-slate-950/50">
                            <input 
                                type="text"
                                placeholder="Search media by name..."
                                value={mediaSearch}
                                onChange={e => setMediaSearch(e.target.value)}
                                className="w-full px-4 py-2 rounded-xl bg-slate-900 border border-slate-800 text-white text-xs placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                        </div>

                        <div className="p-6 overflow-y-auto flex-1">
                            {loadingMedia ? (
                                <div className="py-12 text-center text-slate-400 text-xs">Loading media assets...</div>
                            ) : mediaItems.length === 0 ? (
                                <div className="py-12 text-center text-slate-500 text-xs">No media files found. Use the "+ Upload Image" button above to upload.</div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                    {mediaItems
                                        .filter(m => !mediaSearch || m.original_name.toLowerCase().includes(mediaSearch.toLowerCase()) || m.filename.toLowerCase().includes(mediaSearch.toLowerCase()))
                                        .map(m => {
                                            const itemUrl = m.url || `/storage/${m.path}`;
                                            return (
                                                <div 
                                                    key={m.id}
                                                    onClick={() => handleSelectMediaItem(m)}
                                                    className="group rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 hover:border-emerald-500 transition cursor-pointer p-2 flex flex-col"
                                                >
                                                    <div className="h-28 bg-slate-900 rounded-xl overflow-hidden mb-2">
                                                        <img 
                                                            src={itemUrl} 
                                                            alt={m.alt_text || m.filename} 
                                                            className="w-full h-full object-cover group-hover:scale-105 transition"
                                                        />
                                                    </div>
                                                    <span className="text-[11px] font-semibold text-slate-200 truncate block">
                                                        {m.original_name}
                                                    </span>
                                                    <span className="text-[10px] text-emerald-400 block mt-0.5 font-mono">
                                                        {m.mime_type} • {formatBytes(m.size_bytes)}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                </div>
                            )}
                        </div>

                        <div className="p-4 border-t border-slate-800 bg-slate-950 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setShowMediaModal(false)}
                                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white transition"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
